import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  addSection,
  updateSection,
  getSectionById,
} from "../../../services/sectionsService";
import { validateSection } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { toast } from "react-toastify";
import { handleErrors } from "../../../utils/errorHandler";
import AiAssistantOffcanvas from "../../AI/AiAssistantOffcanvas";
import { SECTION_AI_RESPONSE_FIELD_MAP } from "../../AI/AIResponseFieldMap";
import FormSideDrawer from "../../Common/FormSideDrawer/FormSideDrawer";

const INITIAL_FORM_STATE = {
  sectionName: "",
  sectionSlug: "",
  sectionDescription: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  displayOrder: "",
  isFeatured: false,
  isFeaturedHome: false,
  isPremium: false,
};

const parseBoolFromApi = (data, ...keys) => {
  for (const key of keys) {
    const value = data?.[key];
    if (value !== undefined && value !== null) {
      return value === true || value === "true" || value === 1;
    }
  }
  return false;
};

const INITIAL_ERRORS_STATE = {
  sectionName: "",
  sectionSlug: "",
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
};

const mapSectionResponse = (data) => ({
  sectionName: data.sectionName || data.SectionName || "",
  sectionSlug: data.sectionSlug || data.SectionSlug || "",
  sectionDescription:
    data.sectionDescription || data.SectionDescription || "",
  metaTitle: data.metaTitle || data.MetaTitle || "",
  metaDescription: data.metaDescription || data.MetaDescription || "",
  metaKeywords: data.metaKeywords || data.MetaKeywords || "",
  isFeatured: parseBoolFromApi(
    data,
    "isFeatured",
    "IsFeatured",
    "featured",
    "Featured"
  ),
  isFeaturedHome: parseBoolFromApi(
    data,
    "isFeaturedHome",
    "IsFeaturedHome",
    "featuredHome",
    "FeaturedHome"
  ),
  isPremium: parseBoolFromApi(data, "isPremium", "IsPremium"),
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
        ? String(data.DisplayOrder)
        : "",
});

export const AddSection = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedSection,
  setEditMode,
  isOpen = false,
  onClose,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const slugManuallyEdited = useRef(false);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    slugManuallyEdited.current = false;
  }, []);

  const closeDrawer = useCallback(() => {
    resetForm();
    setSelectedSection?.(null);
    setEditMode?.(false);
    onClose?.();
  }, [resetForm, setSelectedSection, setEditMode, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      const sectionId = initialData?.id ?? initialData?.Id;
      if (editMode && sectionId) {
        try {
          const data = await getSectionById(sectionId);
          setFormData(mapSectionResponse(data));
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
      setErrors(INITIAL_ERRORS_STATE);
    };
    fetchData();
  }, [editMode, initialData, isOpen]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "sectionName") {
      setFormData((prev) => ({
        ...prev,
        sectionName: value,
        ...(slugManuallyEdited.current
          ? {}
          : { sectionSlug: generateSlug(value) }),
      }));
    } else if (name === "sectionSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, sectionSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: Boolean(checked) }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateSection(formData);
      setErrors(validationErrors);
      if (!valid) return;

      const sectionId = initialData?.id ?? initialData?.Id;
      const payload = {
        sectionName: formData.sectionName.trim(),
        sectionSlug: formData.sectionSlug.trim(),
        sectionDescription: formData.sectionDescription?.trim() || "",
        metaTitle: formData.metaTitle?.trim() || "",
        metaDescription: formData.metaDescription?.trim() || "",
        metaKeywords: formData.metaKeywords?.trim() || "",
        featured: Boolean(formData.isFeatured),
        featuredHome: Boolean(formData.isFeaturedHome),
        isPremium: Boolean(formData.isPremium),
        displayOrder: Number(formData.displayOrder),
      };

      if (editMode) {
        payload.id = sectionId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateSection : addSection;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Section ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        onSuccess?.();
        setEditMode?.(false);
        setSelectedSection?.(null);
        onClose?.();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      setSelectedSection,
      onClose,
      resetForm,
    ]
  );

  const buildSectionDynamicFields = useCallback(
    (promptText) => ({
      SectionName: formData.sectionName || "",
      UserPrompt: promptText || "",
    }),
    [formData.sectionName]
  );

  return (
    <>
      <FormSideDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        title={editMode ? "Update Section" : "Add Section"}
        subtitle={
          editMode
            ? "Edit section details and save changes"
            : "Fill in the details to create a new section"
        }
        icon={editMode ? "ri-pencil-line" : "ri-layout-grid-line"}
        width="lg"
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeDrawer}
              disabled={isButtonDisabled}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="section-side-form"
              className="btn btn-secondary"
              disabled={isButtonDisabled}
            >
              {isButtonDisabled
                ? editMode
                  ? "Updating..."
                  : "Saving..."
                : editMode
                  ? "Update"
                  : "Save"}
            </button>
          </>
        }
      >
        <form id="section-side-form" onSubmit={handleSubmit} method="POST">
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="sectionName" className="form-label">
                Section Name <span className="required-field">*</span>
              </label>
              <input
                type="text"
                name="sectionName"
                id="sectionName"
                placeholder="Enter section name"
                value={formData.sectionName}
                onChange={handleInputChange}
                className={`form-control ${
                  errors.sectionName ? "is-invalid" : ""
                }`}
              />
              {errors.sectionName && (
                <div className="invalid-feedback">{errors.sectionName}</div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="sectionSlug" className="form-label">
                Section Slug <span className="required-field">*</span>
              </label>
              <input
                type="text"
                name="sectionSlug"
                id="sectionSlug"
                placeholder="section-slug"
                value={formData.sectionSlug}
                onChange={handleInputChange}
                className={`form-control ${
                  errors.sectionSlug ? "is-invalid" : ""
                }`}
              />
              {errors.sectionSlug && (
                <div className="invalid-feedback">{errors.sectionSlug}</div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="displayOrder" className="form-label">
                Display Order <span className="required-field">*</span>
              </label>
              <input
                type="number"
                name="displayOrder"
                id="displayOrder"
                min="1"
                step="1"
                placeholder="e.g. 1"
                value={formData.displayOrder}
                onChange={handleDisplayOrderChange}
                onKeyDown={handleDisplayOrderKeyDown}
                className={`form-control ${
                  errors.displayOrder ? "is-invalid" : ""
                }`}
              />
              {errors.displayOrder && (
                <div className="invalid-feedback">{errors.displayOrder}</div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="sectionDescription" className="form-label">
                Section Description
              </label>
              <textarea
                name="sectionDescription"
                id="sectionDescription"
                rows="3"
                placeholder="Brief description for this section"
                value={formData.sectionDescription}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-light">
                <p className="text-muted small mb-3 fw-medium">Visibility</p>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label d-block">Featured</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isFeatured"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleToggleChange}
                      />
                      <label className="form-check-label" htmlFor="isFeatured">
                        Show as featured
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label d-block">Featured Home</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isFeaturedHome"
                        name="isFeaturedHome"
                        checked={formData.isFeaturedHome}
                        onChange={handleToggleChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="isFeaturedHome"
                      >
                        Show on home page
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label d-block">Premium</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isPremium"
                        name="isPremium"
                        checked={formData.isPremium}
                        onChange={handleToggleChange}
                      />
                      <label className="form-check-label" htmlFor="isPremium">
                        Mark as premium
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-light">
                <p className="text-muted small mb-3 fw-medium">SEO Settings</p>
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="metaTitle" className="form-label">
                      Meta Title
                    </label>
                    <input
                      type="text"
                      name="metaTitle"
                      id="metaTitle"
                      value={formData.metaTitle}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.metaTitle ? "is-invalid" : ""
                      }`}
                    />
                    {errors.metaTitle && (
                      <div className="invalid-feedback">{errors.metaTitle}</div>
                    )}
                  </div>
                  <div className="col-12">
                    <label htmlFor="metaKeywords" className="form-label">
                      Meta Keywords
                    </label>
                    <input
                      type="text"
                      name="metaKeywords"
                      id="metaKeywords"
                      value={formData.metaKeywords}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="metaDescription" className="form-label">
                      Meta Description
                    </label>
                    <textarea
                      name="metaDescription"
                      id="metaDescription"
                      rows="2"
                      value={formData.metaDescription}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.metaDescription ? "is-invalid" : ""
                      }`}
                    />
                    {errors.metaDescription && (
                      <div className="invalid-feedback">
                        {errors.metaDescription}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormSideDrawer>

      <AiAssistantOffcanvas
        pageLink="manage-sections"
        formData={formData}
        setFormData={setFormData}
        fieldMap={SECTION_AI_RESPONSE_FIELD_MAP}
        buildDynamicFields={buildSectionDynamicFields}
      />
    </>
  );
};
